import { Check, CircleCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import { FixedLayout } from "@/components/shared/fixed-layout";
import { useSignupStore } from "@/lib/store/use-signup-store";

const REQUIRED_TERM_TITLE = `회원가입을 위해
약관동의를 먼저 진행해주세요.`;

interface RequiredTerm {
  id: string;
  title: string;
  link: string;
}

const REQUIRED_TERMS: RequiredTerm[] = [
  {
    id: "terms-of-service",
    title: "(필수) 이용약관 동의",
    link: "/term/terms-of-service",
  },
  {
    id: "privacy-policy",
    title: "(필수) 개인정보 수집 및 이용 동의",
    link: "/term/privacy-policy",
  },
];

interface RequiredTermStepProps {
  onExit?: () => void;
}

export function RequiredTermStep({ onExit }: RequiredTermStepProps) {
  const { termsAgreed, setTermsAgreed, goToNextStep } = useSignupStore();

  const [selectedTerms, setSelectedTerms] = useState<string[]>(
    termsAgreed ? REQUIRED_TERMS.map(({ id }) => id) : []
  );

  const isAllTermsSelected = selectedTerms.length === REQUIRED_TERMS.length;

  const handleSelectTerm = (termId: string) => {
    setSelectedTerms((prev) =>
      prev.includes(termId)
        ? prev.filter((id) => id !== termId)
        : [...prev, termId]
    );
  };

  const handleSelectAllTerms = () => {
    setSelectedTerms(
      selectedTerms.length === REQUIRED_TERMS.length
        ? []
        : REQUIRED_TERMS.map(({ id }) => id)
    );
  };

  const handleNext = () => {
    if (isAllTermsSelected) {
      setTermsAgreed(true);
      setSelectedTerms([]);
      goToNextStep();
    }
  };

  return (
    <FixedLayout
      title={REQUIRED_TERM_TITLE}
      buttonDisabled={!isAllTermsSelected}
      onSubmit={handleNext}
      onBack={onExit}
    >
      <div
        className="flex gap-3 border border-border rounded-md p-3 items-center"
        onClick={handleSelectAllTerms}
      >
        <CircleCheck
          stroke="white"
          fill="currentColor"
          className={cn(
            "size-6 transition-colors duration-200",
            isAllTermsSelected ? "text-primary" : "text-border"
          )}
        />
        <span className="text-sm font-medium text-muted-foreground">
          약관에 모두 동의
        </span>
      </div>
      <div className="flex flex-col gap-2 pt-3.5">
        {REQUIRED_TERMS.map(({ id, title, link }) => (
          <div key={id} className="flex w-full pl-3 py-1.5 gap-3 items-center">
            <div
              onClick={() => handleSelectTerm(id)}
              className="flex items-center gap-3 flex-1"
            >
              <Check
                className={cn(
                  "size-6 transition-colors duration-200",
                  selectedTerms.includes(id) ? "text-primary" : "text-border"
                )}
              />
              <span className="text-sm font-medium text-muted-foreground">
                {title}
              </span>
            </div>
            <Button variant="link" size="sm" asChild>
              <Link to={link}>보기</Link>
            </Button>
          </div>
        ))}
      </div>
    </FixedLayout>
  );
}
